import React, { useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, useWindowDimensions } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useParentFeed } from '../hooks/useParentFeed';
import { AlertRow, alertRowFrom } from '../utils/alertRows';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { parseBackendDate } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';

export function AlertsScreen({ isNested }: { isNested?: boolean } = {}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // The same items as the bell's Recent Notifications (utils/alertRows).
  const { activities, isLoading } = useParentFeed();
  const alerts: AlertRow[] = useMemo(() => activities.map(alertRowFrom), [activities]);

  // A date that never parsed (missing/malformed) must not render "Invalid
  // Date" — say plainly that there's no date rather than showing a broken one.
  // parseBackendDate, not new Date: the API sends timestamps with no timezone
  // marker, which JavaScript reads as local time (see dateUtils).
  const formatDate = (iso: string) => {
    const parsed = parseBackendDate(iso);
    return parsed
      ? parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : 'No date';
  };

  const renderItem = ({ item }: { item: AlertRow }) => (
    <View style={[styles.card, isMobile && styles.cardMobile, { borderLeftColor: item.color }]}>
      <View style={[styles.cardHeader, isMobile && styles.cardHeaderMobile]}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>{item.title}</Text>
          <Text style={styles.date} numberOfLines={1}>
            {formatDate(item.date)}
          </Text>
        </View>
        <View style={[styles.priorityBadge, isMobile && styles.priorityBadgeMobile, { backgroundColor: item.color }]}>
          <Text style={styles.priorityText}>{item.label}</Text>
        </View>
      </View>
      {item.description && (
        <Text style={styles.description} numberOfLines={3}>
          {item.description}
        </Text>
      )}
    </View>
  );

  const content = (
    <View style={{ flexGrow: 1 }}>

      {isLoading ? (
        <LoadingSpinner message="Loading alerts..." />
      ) : alerts.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="bell-off" size={48} color={colors.textMuted} />
          <Text style={styles.emptyText}>No alerts</Text>
        </View>
      ) : (
        <FlatList
          data={alerts}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          scrollEnabled={!isNested}
          ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
        />
      )}
    </View>
  );

  if (isNested) return content;
  return <WebPageShell title="Alerts">{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  header: { fontSize: 22, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, borderLeftWidth: 4, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardMobile: { padding: 12 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  cardHeaderMobile: { flexWrap: 'wrap', gap: 6 },
  title: { fontSize: 14, fontWeight: '700', color: colors.textPrimary, marginBottom: 2 },
  date: { fontSize: 11, color: colors.textMuted },
  priorityBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  priorityBadgeMobile: { paddingHorizontal: 8, paddingVertical: 3 },
  priorityText: { fontSize: 10, color: '#FFF', fontWeight: '700', textTransform: 'capitalize' },
  description: { fontSize: 13, color: colors.textSecondary, lineHeight: 18 },
});
